import { useState, memo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { Box, Button, H6, SelectChangeEvent, Snackbar } from '@parspec/pixel';

import { CoverPage } from '../CommonSections/CoverPage';
import { DocumentOrdering } from '../CommonSections/DocumentOrdering';
import { TableOfContents } from '../CommonSections/TableOfContents';
import { NotesSummary } from '../CommonSections/NotesSummary';
import { HeaderFooter } from '../CommonSections/HeaderFooter';
import { SECTION_BREAKS_AND_KIT_COMPONENTS_SWITCH_VALUE } from '../../utils/constants';
import {
    PRE_FILL_ADDRESSED_TO,
    PRE_FILL_COVER_PAGE_LOGO,
    PRE_FILL_DATE,
    PRE_FILL_PREPARED_BY_LOGO,
    PRE_FILL_PRIMARY_TITLE,
    PRE_FILL_SECONDARY_TITLE
} from '../QuoteTemplatePreferences/utils/constants';
import { useLocationDetails } from '../context';
import { MultipleBranchesModal } from '../../shared/components/MultipleBranchesModal/MultipleBranchesModal';
import { SaveButton } from '../../shared/components/SaveButton';
import { useEditOmSettingsMutation } from '../../queries';
import { EDIT_SETTINGS_SUCCESS_MSG } from '../../shared/constants';
import { useParspecNavigation } from 'src/app/Context';

const OmTemplate = () => {
    const { pNavigate, hasUnsavedChangesRef } = useParspecNavigation()
    const { currentLocationId, omPackageTemplate, setOmPackageTemplate, branchBrandingTemplate, branchLocationsRef } = useLocationDetails();

    const { mutateAsync: editOMPackageData, isLoading } = useEditOmSettingsMutation();

    const location = useLocation();
    const currentTab = location.pathname.split('/').filter(Boolean).pop();


    useEffect(() => {
        const currentBranchLocations = branchLocationsRef.current;
        return () => {
            if (currentBranchLocations?.omPackageTemplate) {
                setOmPackageTemplate(currentBranchLocations.omPackageTemplate)
            }
        }
    }, [branchLocationsRef, setOmPackageTemplate])


    const changeHandler = (event: SelectChangeEvent<unknown>, presetName: string, field_name?: string) => {
        hasUnsavedChangesRef.current = true;
        const fieldName = field_name ? field_name : event.target?.name;

        const isNotesFromCoverPage =
            presetName === 'cover_page_presets' &&
            (fieldName === 'notes' ||
                fieldName === PRE_FILL_PRIMARY_TITLE ||
                fieldName === PRE_FILL_SECONDARY_TITLE ||
                fieldName === PRE_FILL_DATE ||
                fieldName === PRE_FILL_COVER_PAGE_LOGO ||
                fieldName === PRE_FILL_PREPARED_BY_LOGO ||
                fieldName === PRE_FILL_ADDRESSED_TO);

        const isRadioButtonsFromLayout = presetName === 'layout_presets' && !['show_kit_components', 'section_breaks', 'maintain_aspect_ratio'].includes(fieldName);
        const isSectionBreakOrKitComponentsFromLayout = presetName === 'layout_presets' && ['show_kit_components', 'section_breaks', 'maintain_aspect_ratio'].includes(fieldName);
        const isTextValuesFromCoverHeaderFooter = presetName === 'header_footer_presets' && (fieldName === 'pre_fill_job_name' || fieldName === 'pre_fill_header_logo');

        let fieldValue: string | boolean;
        if (isNotesFromCoverPage || isRadioButtonsFromLayout || isTextValuesFromCoverHeaderFooter) {
            fieldValue = event.target?.value as string;
        } else {
            const checkedField = event.target as HTMLInputElement;
            if (isSectionBreakOrKitComponentsFromLayout) {
                fieldValue = checkedField.checked ? SECTION_BREAKS_AND_KIT_COMPONENTS_SWITCH_VALUE.INCLUDED : SECTION_BREAKS_AND_KIT_COMPONENTS_SWITCH_VALUE.EXCLUDED;
            } else {
                fieldValue = checkedField.checked;
            }
        }

        setOmPackageTemplate(() => ({ ...omPackageTemplate, [presetName]: { ...omPackageTemplate[presetName], [fieldName]: fieldValue } }));
    };

    const handleTemplateSelection = (_e: React.MouseEvent<HTMLElement>, template: string) => {
        hasUnsavedChangesRef.current = true;
        switch (template) {
            case '1':
                setOmPackageTemplate(() => ({
                    ...omPackageTemplate,
                    cover_page_template_id: 1
                }));
                break;
            case '2':
                setOmPackageTemplate(() => ({
                    ...omPackageTemplate,
                    cover_page_template_id: 2
                }));
                break;
            default:
                break;
        }
    };

    const resetDataHandler = () => {
        pNavigate(`/v2/settings/company-locations`);
    };

    const [isMultipleBranchesModalOpen, setIsMultipleBranchesModalOpen] = useState(false);
    const [snackbarData, setSnackbarData] = useState('');

    const onOptionSelection = async (type: string) => {
        switch (type) {
            case 'single':
                const payload = {
                    apply_settings_to_company_ids: [currentLocationId], payload: omPackageTemplate
                }
                try {
                    hasUnsavedChangesRef.current = false;
                    await editOMPackageData({ data: payload });
                    setSnackbarData(() => EDIT_SETTINGS_SUCCESS_MSG)
                } catch (err: any) {
                    setSnackbarData(() => err?.response?.data?.message ? err?.response?.data?.message : 'Something went wrong, please try again');
                }
                break;

            case 'multiple':
                setIsMultipleBranchesModalOpen(() => true);
                break;
        }
    };
    const onColumnSelection = async (selectedBranchIdsArr: number[]) => {
        const payload = {
            apply_settings_to_company_ids: selectedBranchIdsArr, payload: omPackageTemplate
        }
        try {
            hasUnsavedChangesRef.current = false;
            await editOMPackageData({ data: payload });
            setSnackbarData(() => EDIT_SETTINGS_SUCCESS_MSG)
        } catch (err: any) {
            setSnackbarData(() => err?.response?.data?.message ? err?.response?.data?.message : 'Something went wrong, please try again');
        }
        setIsMultipleBranchesModalOpen(() => false);
    };

    const handleCloseMultipleBranchesModal = () => {
        setIsMultipleBranchesModalOpen(() => false);
    }

    return (
        <Box bgcolor="primary.contrastText">
            <Box display="flex">
                <Box>
                    <H6>O&M Package Template Preferences</H6>
                </Box>
                <Box ml="auto" display="flex">
                    <Box display="flex">
                        <Box mr={2}>
                            <Button variant="outlined" color="secondary" onClick={resetDataHandler}>
                                Cancel
                            </Button>
                        </Box>
                        <Box>
                            <SaveButton onOptionSelection={onOptionSelection} isLoading={isLoading} />
                        </Box>
                    </Box>
                </Box>
            </Box>

            <Box>
                <Box mt={6}>
                    <CoverPage
                        changeHandler={changeHandler}
                        handleTemplateSelection={handleTemplateSelection}
                        templateState={omPackageTemplate}
                        branchBrandingState={branchBrandingTemplate}
                        templateName={'OM'}
                    />
                    <DocumentOrdering changeHandler={changeHandler} templateState={omPackageTemplate} />
                    <TableOfContents changeHandler={changeHandler} templateState={omPackageTemplate} templateName={'OM'} />
                    <NotesSummary changeHandler={changeHandler} templateState={omPackageTemplate} templateName={'OM'} />
                    <HeaderFooter changeHandler={changeHandler} templateState={omPackageTemplate} templateName={'OM'} />
                </Box>
            </Box>

            <MultipleBranchesModal
                isOpen={isMultipleBranchesModalOpen}
                onClose={handleCloseMultipleBranchesModal}
                currentTab={currentTab}
                onColumnSelection={onColumnSelection}
                isLoading={isLoading}

            />

            {<Snackbar open={snackbarData?.length > 0} message={snackbarData} onClose={() => setSnackbarData(() => '')} />}

        </Box>
    );
};

export default memo(OmTemplate);
